import { getFilteredTemplateConfigs } from '@/config/templateConfigs';
import { useUserData } from '@/contexts/UserDataContext';
import { type ProcessedImage } from '@/utils/types';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { AppPresentationVideo } from '../../components/AppPresentationVideo';
import { Button } from '../../components/Button';
import { DownloadOnAppStore } from '../../components/DownloadOnAppStore';
import { DownloadOnGooglePlay } from '../../components/DownloadOnGooglePlay';
import { ProgressBar } from '../../components/ProgressBar';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { TemplateProcessor } from '../../components/TemplateProcessor';

interface ProcessingStepProps {
    onNext: (images: ProcessedImage[]) => void;
}

export const ProcessingStep = ({ onNext }: ProcessingStepProps) => {
    const { userData } = useUserData();
    const [processedImages, setProcessedImages] = useState<ProcessedImage[]>([]);
    const [isProgressComplete, setIsProgressComplete] = useState(false);
    const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });
    const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [hasStartedProcessing, setHasStartedProcessing] = useState(false);
    const [processingError, setProcessingError] = useState<Error | null>(null);
    const [isFirstTemplateFirstPass, setIsFirstTemplateFirstPass] = useState(true);

    // Get filtered template configs based on user data
    const templateConfigs = useMemo(() => getFilteredTemplateConfigs(userData), [userData]);

    const handleNext = useCallback(() => {
        onNext(processedImages);
    }, [onNext, processedImages]);

    const handleProgressComplete = useCallback(() => {
        setIsProgressComplete(true);
    }, []);

    const handleTemplateComplete = useCallback((image: ProcessedImage) => {
        // If this is the first template's first pass, discard the result and re-process
        if (currentTemplateIndex === 0 && isFirstTemplateFirstPass) {
            console.log('First template first pass complete - processing again...');
            setIsFirstTemplateFirstPass(false);
            // Don't increment anything, just re-render to trigger processing again
            setIsProcessing(false);
            return;
        }

        // For all other cases (second pass of first template or any other template), save the result
        setProcessedImages(prev => [...prev, image]);
        setProcessingProgress(prev => ({ ...prev, current: prev.current + 1 }));

        // Move to next template
        setCurrentTemplateIndex(prev => prev + 1);
    }, [currentTemplateIndex, isFirstTemplateFirstPass]);

    const handleTemplateError = useCallback((error: Error) => {
        console.error('Template processing error:', error);
        // Stop processing and display error
        setProcessingError(error);
        setIsProcessing(false);
    }, []);


    // Initialize processing
    useEffect(() => {
        if (!hasStartedProcessing && templateConfigs.length > 0) {
            setHasStartedProcessing(true);
            setIsProcessing(true);
            setProcessingProgress({ current: 0, total: templateConfigs.length });
        }
    }, [hasStartedProcessing, templateConfigs.length]);

    // Check if processing is complete
    useEffect(() => {
        if (currentTemplateIndex >= templateConfigs.length && templateConfigs.length > 0) {
            setIsProcessing(false);
        }
    }, [currentTemplateIndex, templateConfigs.length]);

    // Re-start processing after first template's first pass
    useEffect(() => {
        if (currentTemplateIndex === 0 && !isFirstTemplateFirstPass && !isProcessing && hasStartedProcessing) {
            console.log('Restarting first template processing (second pass)...');
            setIsProcessing(true);
        }
    }, [currentTemplateIndex, isFirstTemplateFirstPass, isProcessing, hasStartedProcessing]);

    const processingDone = isProgressComplete && processedImages.length >= templateConfigs.length;

    return (
        <ErrorBoundary>
            <div className="flex-1 flex flex-col items-center justify-start overflow-hidden px-8">
                <AppPresentationVideo />
            </div>
            <div className="flex flex-col justify-center items-center gap-1 w-full px-8">
                <div className="flex justify-center items-center gap-1 w-full">
                    <DownloadOnAppStore />
                    <DownloadOnGooglePlay />
                </div>

                {processingError ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 w-full max-w-md">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">!</span>
                            </div>
                            <h3 className="text-red-800 font-semibold">Processing Error</h3>
                        </div>
                        <p className="text-red-700 text-sm">
                            {processingError.message || 'An error occurred while processing the template.'}
                        </p>
                    </div>
                ) : (
                    <>
                        <Button
                            label={processingDone ? "Continue" : isProcessing ? `Processing ${processingProgress.current}/${processingProgress.total}...` : "Preparing images..."}
                            onClick={handleNext}
                            inactive={!processingDone}
                        />
                        <div className="w-full px-4">
                            <ProgressBar
                                duration={8}
                                onComplete={handleProgressComplete}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* Template processor - render only current template */}
            {isProcessing && currentTemplateIndex < templateConfigs.length && (
                <TemplateProcessor
                    key={`${templateConfigs[currentTemplateIndex].name}-${currentTemplateIndex}-${currentTemplateIndex === 0 ? isFirstTemplateFirstPass : 'na'}`}
                    templateConfig={templateConfigs[currentTemplateIndex]}
                    userData={userData}
                    onComplete={handleTemplateComplete}
                    onError={handleTemplateError}
                    autoProcess={true}
                />
            )}
        </ErrorBoundary>
    );
};
